// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
//import { useDispatch } from 'react-redux'

import { type Holding } from '@shared/data-access/interfaces/api/ai/holding';
import {
  type PortfolioAnalysis,
  type StockAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import type { KpiBoxesProps } from '@shared/ui-kit/components';
import { destructureHoldings } from '@shared/ui-kit/utils/destructure-holdings';
import { formatToMoney } from './format-to-money';
import { type PositionsAnalysis } from '@shared/data-access/interfaces/redux/portfolio-state';
import { type WorkflowState } from '@shared/data-access/interfaces/redux/workflow-state';
import { type TradesOperations } from '@shared/data-access/interfaces/redux/trades';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

export type KpiTranslationKey =
  | 'common.investment'
  | 'common.volatility'
  | 'common.unrealized-gain'
  | 'common.harvest-opportunity'
  | 'common.net-new-investment'
  | 'common.buy'
  | 'common.sell'
  | 'common.realized-gains'
  | 'common.tracking-error';

export const getKpisData = (
  positions: PositionsAnalysis[],
  holdings: Holding[],
  workflow: WorkflowState,
  analysis: PortfolioAnalysis | null,
): KpiBoxesProps[] => {
  const tradesByOperation = (
    trades: PositionsAnalysis[],
    operation: TradesOperations,
  ) => {
    return trades.filter(item => item.operation === operation)?.map(item => item);
  };

  let kpiData: Array<Omit<KpiBoxesProps, 'text'> & { text: KpiTranslationKey }> =
    [];

  const { outstandingBalance, unrealizedCapitalGains } =
    destructureHoldings(holdings);

  if (
    (workflow.type === 'load' ||
      workflow.type === 'asset-allocation' ||
      workflow.type === 'research') &&
    analysis
  ) {
    const outstandingBalanceTotal = outstandingBalance.reduce(
      (accumulator, curr) => accumulator + curr,
      0,
    );

    const unrealizedCapitalGainsTotal = unrealizedCapitalGains.reduce(
      (accumulator, curr) => accumulator + curr,
      0,
    );

    const negative = unrealizedCapitalGains.filter(function (a) {
      return a <= 0;
    });

    const unrealizedCapitalGainsTotalNegative = negative.reduce(
      (accumulator, curr) => accumulator + curr,
      0,
    );

    const { tracking_expected, risk } = analysis as StockAnalysis;

    kpiData = [
      {
        value: formatToMoney(Math.trunc(outstandingBalanceTotal), undefined, 0),
        text: 'common.investment',
        numberPositions: outstandingBalance.length,
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        value:
          workflow.asset === 'stock'
            ? `${(
                tracking_expected.expected_tracking_kpis.tracking_error * 100
              ).toFixed(2)} %`
            : `${(risk.volatility * 100).toFixed(2)} %`,
        text:
          workflow.asset === 'stock'
            ? 'common.tracking-error'
            : 'common.volatility',
        popupName: workflow.asset === 'stock' ? 'charts.trackingError' : undefined,
        icon: 'Activity',
        color: 'secondary',
      },
      {
        value: formatToMoney(unrealizedCapitalGainsTotal),
        text: 'common.unrealized-gain',
        popupName: 'kpis.unrealizedGains',
        icon: 'DollarSign',
        color: 'main',
      },
      {
        value: formatToMoney(unrealizedCapitalGainsTotalNegative),
        text: 'common.harvest-opportunity',
        popupName: 'kpis.harvestOpportunity',
        icon: 'Zap',
        color: 'success',
      },
    ];
  } else {
    const buyTrades = tradesByOperation(positions, OperationType.BUY);

    const buyTotal = buyTrades.reduce(
      (accumulator, curr) => accumulator + curr.volume,
      0,
    );

    const sellTrades = tradesByOperation(positions, OperationType.SELL);

    const sellTotal = sellTrades.reduce(
      (accumulator, curr) => accumulator + curr.volume,
      0,
    );

    const realizedCapitalGainsTotal = sellTrades
      ?.map(item => item.unrealized_capital_gains)
      .reduce((accumulator, curr) => accumulator + curr, 0);

    kpiData = [
      {
        value: formatToMoney(Math.trunc(buyTotal - sellTotal), undefined, 0),
        text: 'common.net-new-investment',
        numberPositions: positions.length || undefined,
        icon: 'TrendingUp',
        color: 'main',
      },
      {
        value: formatToMoney(Math.trunc(buyTotal)),
        text: 'common.buy',
        numberPositions: buyTrades.length || undefined,
        icon: 'CircleArrowUp',
        color: 'success',
      },
      {
        value: formatToMoney(Math.trunc(sellTotal)),
        text: 'common.sell',
        numberPositions: sellTrades.length || undefined,
        icon: 'CircleArrowDown',
        color: 'danger',
      },
      {
        value: formatToMoney(Math.trunc(realizedCapitalGainsTotal)),
        text: 'common.realized-gains',
        popupName: 'kpis.realizedGains',
        icon: 'DollarSign',
        color: 'success',
      },
    ];
  }

  return kpiData;
};
